import React, { useEffect, useRef, useState, useCallback } from 'react';
import { waitForKakaoMap } from '../utils/kakaoLoader';
import './KakaoMap.css';
import blueDotMarker from '../marker/blue dot.png';
import restaurantMarkerImg from '../marker/restaurant.png';
import cafeMarkerImg from '../marker/cafe.png';

interface KakaoMapProps {
  latitude: number;
  longitude: number;
  level?: number;
  className?: string;
  categoryCode?: string;
  distance?: number;
  onRestaurantsUpdate?: (restaurants: any[]) => void;
}

const KakaoMap: React.FC<KakaoMapProps> = ({ 
  latitude, 
  longitude, 
  level = 3, 
  className = '',
  categoryCode = 'FD6',
  distance = 1.5,
  onRestaurantsUpdate
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerInstance = useRef<any>(null);
  const placesService = useRef<any>(null);
  const infoWindow = useRef<any>(null);
  const restaurantMarkers = useRef<any[]>([]);
  const restaurantData = useRef<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 음식점 마커들을 제거하는 함수
  const clearRestaurantMarkers = useCallback(() => {
    restaurantMarkers.current.forEach(marker => {
      marker.setMap(null);
    });
    restaurantMarkers.current = [];
  }, []);

  // 음식점 마커를 표시하는 함수
  const displayRestaurantMarker = useCallback((place: any) => {
    if (!mapInstance.current || !infoWindow.current) return;

    // 카테고리에 따라 마커 이미지 선택
    const markerImageSrc = categoryCode === 'CE7' ? cafeMarkerImg : restaurantMarkerImg;
    const markerImageSize = new window.kakao.maps.Size(60, 60);
    const markerImageOption = { offset: new window.kakao.maps.Point(30, 28) };
    const markerImage = new window.kakao.maps.MarkerImage(markerImageSrc, markerImageSize, markerImageOption);

    // 마커를 생성하고 지도에 표시합니다
    const marker = new window.kakao.maps.Marker({
      map: mapInstance.current,
      position: new window.kakao.maps.LatLng(place.y, place.x),
      image: markerImage
    });

    // 마커에 클릭 이벤트를 등록합니다
    window.kakao.maps.event.addListener(marker, 'click', () => {
      // 인포윈도우 내용 구성
      const content = createInfoWindowContent(place);
      infoWindow.current.setContent(content);
      infoWindow.current.open(mapInstance.current, marker);
    });

    // 생성된 마커를 배열에 추가
    restaurantMarkers.current.push(marker);
  }, [categoryCode]);

  // 인포윈도우 내용을 생성하는 함수
  const createInfoWindowContent = (place: any) => {
    // 카테고리명 (예: 음식점 > 한식 > 삼겹살)
    const category = place.category_name ? place.category_name.split(' > ').pop() : '';
    
    // 전화번호
    const phone = place.phone || '';
    
    // 주소 (도로명 주소 우선, 없으면 지번 주소)
    const address = place.road_address_name || place.address_name || '';

    // 카카오맵 상세 페이지 링크
    const placeUrl = place.place_url || '';

    return `
      <div style="position:relative;padding:16px 32px 16px 16px;width:280px;max-width:280px;font-family:'Pretendard',-apple-system,BlinkMacSystemFont,system-ui,Roboto,sans-serif;box-sizing:border-box;">
        <button 
          onclick="window.closeInfoWindow()"
          style="position:absolute;top:8px;right:8px;width:24px;height:24px;border:none;background:#f0f0f0;color:#666;border-radius:50%;cursor:pointer;font-size:16px;line-height:1;padding:0;display:flex;align-items:center;justify-content:center;font-weight:bold;"
          onmouseover="this.style.background='#e0e0e0'"
          onmouseout="this.style.background='#f0f0f0'"
        >
          ✕
        </button>
        
        <div style="font-size:15px;font-weight:700;color:#333;margin-bottom:10px;padding-right:8px;word-break:keep-all;line-height:1.4;">
          ${place.place_name}
        </div>
        
        ${category ? `
          <div style="font-size:12px;color:#666;margin-bottom:6px;display:flex;align-items:center;">
            <span style="margin-right:4px;">🏷️</span>
            <span>${category}</span>
          </div>
        ` : ''}
        
        ${address ? `
          <div style="font-size:11px;color:#666;margin-bottom:6px;line-height:1.5;word-break:keep-all;display:flex;align-items:flex-start;">
            <span style="margin-right:4px;flex-shrink:0;">📍</span>
            <span style="flex:1;">${address}</span>
          </div>
        ` : ''}
        
        ${phone ? `
          <div style="font-size:12px;color:#666;margin-bottom:10px;display:flex;align-items:center;">
            <span style="margin-right:4px;">📞</span>
            <span>${phone}</span>
          </div>
        ` : ''}
        
        ${placeUrl ? `
          <a href="${placeUrl}" target="_blank" rel="noopener noreferrer" 
             style="display:block;width:100%;background:#FEE500;color:#000;text-decoration:none;padding:8px 12px;border-radius:6px;font-size:12px;font-weight:600;text-align:center;box-sizing:border-box;margin-top:8px;">
            카카오맵에서 보기 ➜
          </a>
        ` : ''}
      </div>
    `;
  };

  // 거리 필터링 로직 - 요구사항에 맞게 데이터 선택
  const filterPlacesByDistance = useCallback((places: any[]) => {
    // 현재 위치 좌표
    const currentLat = latitude;
    const currentLng = longitude;

    // 각 장소에 거리 계산 추가
    const placesWithDistance = places.map(place => {
      const distanceInMeters = calculateDistance(
        currentLat, 
        currentLng, 
        parseFloat(place.y), 
        parseFloat(place.x)
      );
      return {
        ...place,
        distanceFromCurrent: distanceInMeters
      };
    });

    // 거리순으로 정렬
    placesWithDistance.sort((a, b) => a.distanceFromCurrent - b.distanceFromCurrent);

    console.log(`총 ${placesWithDistance.length}개의 장소 검색 완료`);

    let filteredPlaces: any[] = [];

    if (distance === 0.5) {
      // 500m 필터: 가까운 순으로 45개
      filteredPlaces = placesWithDistance.slice(0, 45);
    } else if (distance === 1.5) {
      // 1.5km 필터: 30개 가까운 순 + 15개 (500m~1.5km)
      const nearest = placesWithDistance.filter(p => p.distanceFromCurrent <= 1500);
      const between500and1500 = placesWithDistance.filter(p => 
        p.distanceFromCurrent > 500 && p.distanceFromCurrent <= 1500
      );

      // 가까운 순 30개 선택
      const first30 = nearest.slice(0, 30);
      
      // 500m~1.5km 사이에서 15개 선택
      const next15 = between500and1500.slice(0, 15);
      
      filteredPlaces = [...first30, ...next15];
      
      // 45개 미만이면 나머지로 채우기
      if (filteredPlaces.length < 45) {
        const remaining = placesWithDistance.filter(p => 
          !filteredPlaces.some(fp => fp.id === p.id)
        );
        filteredPlaces = [...filteredPlaces, ...remaining].slice(0, 45);
      }
    } else if (distance === 3) {
      // 3km 필터: 15개 가까운 순 + 15개 (500m~1.5km) + 15개 (1.5km~3km)
      const nearest = placesWithDistance.filter(p => p.distanceFromCurrent <= 3000);
      const between500and1500 = placesWithDistance.filter(p => 
        p.distanceFromCurrent > 500 && p.distanceFromCurrent <= 1500
      );
      const between1500and3000 = placesWithDistance.filter(p => 
        p.distanceFromCurrent > 1500 && p.distanceFromCurrent <= 3000
      );

      // 각 구간에서 선택
      const first15 = nearest.slice(0, 15);
      const next15 = between500and1500.slice(0, 15);
      const last15 = between1500and3000.slice(0, 15);
      
      filteredPlaces = [...first15, ...next15, ...last15];
      
      // 45개 미만이면 나머지로 채우기
      if (filteredPlaces.length < 45) {
        const remaining = placesWithDistance.filter(p => 
          !filteredPlaces.some(fp => fp.id === p.id)
        );
        filteredPlaces = [...filteredPlaces, ...remaining].slice(0, 45);
      }
    } else {
      // 기타 거리의 경우 가까운 순으로 45개
      filteredPlaces = placesWithDistance.slice(0, 45);
    }

    console.log(`필터링 결과: ${filteredPlaces.length}개 (거리: ${distance}km)`);
    return filteredPlaces;
  }, [latitude, longitude, distance]);

  // 두 좌표 사이의 거리를 계산하는 함수 (미터 단위)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371e3; // 지구 반지름 (미터)
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // 미터 단위 거리
  };

  // 주변 장소를 검색하는 함수 (모든 페이지 결과 가져오기)
  const searchNearbyRestaurants = useCallback(() => {
    if (!placesService.current || !mapInstance.current) return;

    // 기존 마커들 제거
    clearRestaurantMarkers();

    // 현재 위치 좌표 생성
    const currentLocation = new window.kakao.maps.LatLng(latitude, longitude);

    // km를 미터로 변환
    const radiusInMeters = distance * 1000;

    // 모든 검색 결과를 저장할 배열
    let allPlaces: any[] = [];

    // 페이지네이션을 통해 모든 페이지의 결과를 가져오는 함수
    const searchCallback = (data: any, status: any, pagination: any) => {
      if (status === window.kakao.maps.services.Status.OK) {
        // 현재 페이지의 데이터를 누적
        allPlaces = [...allPlaces, ...data];
        
        console.log(`페이지 ${pagination.current}/${pagination.last} 로드 완료 (${data.length}개)`);
        
        // 다음 페이지가 있으면 계속 검색
        if (pagination.hasNextPage) {
          pagination.nextPage();
        } else {
          // 모든 페이지 검색 완료 후 거리 필터링 적용
          const filteredPlaces = filterPlacesByDistance(allPlaces);
          
          // 검색된 데이터 저장
          restaurantData.current = filteredPlaces;
          
          // 부모 컴포넌트에 데이터 전달
          if (onRestaurantsUpdate) {
            onRestaurantsUpdate(filteredPlaces);
          }
          
          // 필터링된 장소들에 마커를 표시합니다
          filteredPlaces.forEach((place: any) => {
            displayRestaurantMarker(place);
          });
        }
      }
    };

    // 카테고리로 장소를 지정된 반경 내에서 검색합니다
    placesService.current.categorySearch(
      categoryCode,
      searchCallback,
      {
        location: currentLocation, // 검색 중심 좌표
        radius: radiusInMeters // 반경 (미터 단위)
      }
    );
  }, [latitude, longitude, categoryCode, distance, onRestaurantsUpdate, clearRestaurantMarkers, displayRestaurantMarker, filterPlacesByDistance]);

  // 카카오맵 초기화
  useEffect(() => {
    const initializeMap = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 카카오맵 SDK 로드 대기
        await waitForKakaoMap();

        // 맵 컨테이너가 없으면 에러
        if (!mapContainer.current) {
          throw new Error('맵 컨테이너를 찾을 수 없습니다.');
        }

        // 카카오맵 API가 완전히 로드되었는지 확인
        if (!window.kakao || !window.kakao.maps) {
          throw new Error('카카오맵 API가 완전히 로드되지 않았습니다.');
        }

        // 지도 옵션 설정
        const mapOption = {
          center: new window.kakao.maps.LatLng(latitude, longitude),
          level: level
        };

        // 지도 생성
        mapInstance.current = new window.kakao.maps.Map(mapContainer.current, mapOption);

        // 현재 위치 마커 생성 및 표시 (blue dot 이미지)
        const markerPosition = new window.kakao.maps.LatLng(latitude, longitude);
        
        // blue dot 마커 이미지 설정
        const imageSrc = blueDotMarker;
        const imageSize = new window.kakao.maps.Size(30, 40);
        const imageOption = { offset: new window.kakao.maps.Point(15, 30) };
        const markerImage = new window.kakao.maps.MarkerImage(imageSrc, imageSize, imageOption);
        
        markerInstance.current = new window.kakao.maps.Marker({
          position: markerPosition,
          image: markerImage
        });
        
        // 마커를 지도 위에 표시
        markerInstance.current.setMap(mapInstance.current);

        // 인포윈도우 생성
        infoWindow.current = new window.kakao.maps.InfoWindow({ zIndex: 1 });

        // 인포윈도우 닫기 함수를 전역으로 등록
        (window as any).closeInfoWindow = () => {
          if (infoWindow.current) {
            infoWindow.current.close();
          }
        };

        // 장소 검색 객체 생성
        placesService.current = new window.kakao.maps.services.Places();

        // 주변 장소 검색
        searchNearbyRestaurants();

        setIsLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '지도를 로드할 수 없습니다.';
        setError(errorMessage);
        setIsLoading(false);
      }
    };

    initializeMap();

    // 컴포넌트 언마운트 시 정리
    return () => {
      // 음식점 마커들 제거
      clearRestaurantMarkers();

      // 현재 위치 마커 제거
      if (markerInstance.current) {
        markerInstance.current.setMap(null);
        markerInstance.current = null;
      }

      // 인포윈도우 제거
      if (infoWindow.current) {
        infoWindow.current = null;
      }

      // Places 서비스 제거
      if (placesService.current) {
        placesService.current = null;
      }
      
      // 지도 인스턴스 제거
      if (mapInstance.current) {
        mapInstance.current = null;
      }

      // 전역 함수 제거
      delete (window as any).closeInfoWindow;
    };
  }, [latitude, longitude, level, searchNearbyRestaurants, clearRestaurantMarkers]); // 초기 로드 및 위치/레벨 변경 시

  // 위치 변경 시 맵 중심 및 마커 위치 업데이트, 주변 음식점 재검색
  useEffect(() => {
    if (mapInstance.current && window.kakao && window.kakao.maps) {
      const newCenter = new window.kakao.maps.LatLng(latitude, longitude);
      mapInstance.current.setCenter(newCenter);
      
      // 현재 위치 마커 위치 업데이트
      if (markerInstance.current) {
        const newMarkerPosition = new window.kakao.maps.LatLng(latitude, longitude);
        markerInstance.current.setPosition(newMarkerPosition);
      } else {
        // 마커가 없으면 새로 생성 (blue dot 이미지)
        const markerPosition = new window.kakao.maps.LatLng(latitude, longitude);
        
        const imageSrc = blueDotMarker;
        const imageSize = new window.kakao.maps.Size(30, 30);
        const imageOption = { offset: new window.kakao.maps.Point(15, 30) };
        const markerImage = new window.kakao.maps.MarkerImage(imageSrc, imageSize, imageOption);
        
        markerInstance.current = new window.kakao.maps.Marker({
          position: markerPosition,
          image: markerImage
        });
        markerInstance.current.setMap(mapInstance.current);
      }

      // 위치가 변경되면 주변 장소 다시 검색
      searchNearbyRestaurants();
    }
  }, [latitude, longitude, categoryCode, searchNearbyRestaurants]);

  // 레벨 변경 시 맵 레벨 업데이트
  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setLevel(level);
    }
  }, [level]);

  if (error) {
    return (
      <div className={`kakao-map-error ${className}`}>
        <div className="error-content">
          <p>⚠️ {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`kakao-map-container ${className}`}>
      {isLoading && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>지도를 불러오는 중...</p>
        </div>
      )}
      <div 
        ref={mapContainer} 
        className={`kakao-map ${isLoading ? 'loading' : ''}`}
        style={{ 
          width: '100%', 
          height: '300px',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      />
    </div>
  );
};

export default KakaoMap;
